import { TIMESHEET_LOCK_PERIODS } from '@clockwise/shared';
import { z } from 'zod';

export const TimesheetSettingsSchema = z.object({
  lockEnabled: z.boolean(),
  lockPeriod: z.enum(TIMESHEET_LOCK_PERIODS),
  graceDays: z.number().int(),
  /** Further working days settlement gets on top of `graceDays` — its
   *  window is `graceDays + settleDays` working days long. */
  settleDays: z.number().int(),
  /** Computed server-side: the next SUBMISSION DEADLINE (ISO YYYY-MM-DD) —
   *  when the period stops accepting new time — or null when locking is off
   *  (nothing to schedule). */
  nextLockDate: z.string().nullable(),
  /**
   * The next SETTLEMENT deadline (ISO YYYY-MM-DD) — the hard close. Equal to
   * `nextLockDate` when no settlement period is configured, and null when
   * locking is off.
   *
   * NOT necessarily the same period as `nextLockDate`, and it can be EARLIER.
   * Each boundary's next deadline is the earliest one still ahead of it, and
   * the settlement window overhangs the submission one — so for `settleDays`
   * business days after a period stops accepting new time, that period is
   * still settling while the submission deadline has already moved on to the
   * following period. Weekly, `graceDays: 1`, `settleDays: 2`, as of Tue
   * 2026-09-01: the next submission deadline is 2026-09-08 (week of 31 Aug),
   * the next settlement one is 2026-09-03 (week of 24 Aug).
   *
   * So do NOT assume `nextSettleDate > nextLockDate`. It holds outside that
   * overhang and is false inside it.
   */
  nextSettleDate: z.string().nullable(),
  /** ISO timestamp of the last save, or null when never saved. */
  updatedAt: z.string().nullable(),
});
export type TimesheetSettings = z.infer<typeof TimesheetSettingsSchema>;

export const UpdateTimesheetSettingsInput = z.object({
  lockEnabled: z.boolean(),
  lockPeriod: z.enum(TIMESHEET_LOCK_PERIODS),
  graceDays: z.number().int().min(0).max(365),
  settleDays: z.number().int().min(0).max(365),
});
export type UpdateTimesheetSettingsInput = z.infer<typeof UpdateTimesheetSettingsInput>;
